import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Radio, Copy, Check, Square, Play, Tv, 
  ChevronDown, ChevronUp, RefreshCw, Wifi, WifiOff,
  MonitorPlay, ArrowRight, QrCode, Camera, CameraOff, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import StreamShareModal from './StreamShareModal';
import CameraPreview from './CameraPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CourtStreamPanel - Tournament mode panel for the Operator Console.
 * Shows court stream status, lets the operator activate/end games on a court.
 */
export default function CourtStreamPanel({ 
  gameId, 
  eventId, 
  token, 
  onGameActivated, 
  onGameEnded 
}) {
  const [courtStreams, setCourtStreams] = useState([]);
  const [activeCourtStream, setActiveCourtStream] = useState(null);
  const [courtGames, setCourtGames] = useState([]);
  const [streamConfig, setStreamConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [ending, setEnding] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [streamStatus, setStreamStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  const authHeaders = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };

  // Fetch event stream config
  const fetchStreamConfig = useCallback(async () => {
    if (!eventId) return null;
    try {
      const res = await fetch(`${API_URL}/api/court-streams/event/${eventId}/config`, {
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setStreamConfig(data);
        return data;
      }
    } catch (err) {
      console.error('Error fetching stream config:', err);
    }
    return null;
  }, [eventId, token]);

  // Fetch court streams for this event
  const fetchCourtStreams = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await fetch(`${API_URL}/api/court-streams/event/${eventId}`, {
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setCourtStreams(data);
        
        // Find the court stream that has this game active, or find by court match
        const activeStream = data.find(cs => cs.current_game_id === gameId);
        if (activeStream) {
          setActiveCourtStream(activeStream);
        } else if (data.length === 1) {
          // If only one court, use it
          setActiveCourtStream(data[0]);
        } else {
          setActiveCourtStream(data[0] || null);
        }
        return data;
      }
    } catch (err) {
      console.error('Error fetching court streams:', err);
    }
    return [];
  }, [eventId, gameId, token]);

  // Fetch games scheduled for this court
  const fetchCourtGames = useCallback(async (courtStreamId) => {
    if (!courtStreamId) return;
    try {
      const res = await fetch(`${API_URL}/api/court-streams/${courtStreamId}/games`, {
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setCourtGames(data.games || []);
      }
    } catch (err) {
      console.error('Error fetching court games:', err);
    }
  }, [token]);

  // Check Cloudflare stream status
  const checkStreamStatus = useCallback(async (cloudflareUid) => {
    if (!cloudflareUid) return;
    setCheckingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/cloudflare/live-inputs/${cloudflareUid}/status`);
      if (res.ok) {
        const data = await res.json();
        setStreamStatus(data);
      }
    } catch (err) {
      console.error('Error checking stream status:', err);
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const config = await fetchStreamConfig();
      if (config?.stream_mode === 'per_court') {
        await fetchCourtStreams();
      }
      setLoading(false);
    };
    load();
  }, [fetchStreamConfig, fetchCourtStreams]);

  // Fetch games when active court changes
  useEffect(() => {
    if (activeCourtStream?.court_stream_id) {
      fetchCourtGames(activeCourtStream.court_stream_id);
      if (activeCourtStream.cloudflare_uid) {
        checkStreamStatus(activeCourtStream.cloudflare_uid);
      }
    }
  }, [activeCourtStream?.court_stream_id, fetchCourtGames, checkStreamStatus]);

  // Auto-refresh stream status every 15 seconds
  useEffect(() => {
    if (!activeCourtStream?.cloudflare_uid) return;
    const interval = setInterval(() => {
      checkStreamStatus(activeCourtStream.cloudflare_uid);
    }, 15000);
    return () => clearInterval(interval);
  }, [activeCourtStream?.cloudflare_uid, checkStreamStatus]);

  // Activate this game on the court stream
  const activateGame = async () => {
    if (!activeCourtStream || !gameId) return;
    setActivating(true);
    try {
      const res = await fetch(
        `${API_URL}/api/court-streams/${activeCourtStream.court_stream_id}/activate-game`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ game_id: gameId })
        }
      );

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Game activated on court stream');
        await fetchCourtStreams();
        await fetchCourtGames(activeCourtStream.court_stream_id);
        onGameActivated?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to activate game');
      }
    } catch (err) {
      toast.error('Error activating game on court');
    } finally {
      setActivating(false);
    }
  };

  // End the current game on the court stream (FINALIZE — requires confirm)
  const endGame = async () => {
    if (!activeCourtStream) return;
    setEnding(true);
    try {
      const res = await fetch(
        `${API_URL}/api/court-streams/${activeCourtStream.court_stream_id}/end-game`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ confirm_final: true, keep_live: false })
        }
      );

      if (res.ok) {
        const data = await res.json();
        toast.success(`Game ended. VOD saved. Final: ${data.final_score}`);
        await fetchCourtStreams();
        await fetchCourtGames(activeCourtStream.court_stream_id);
        onGameEnded?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to end game');
      }
    } catch (err) {
      toast.error('Error ending game');
    } finally {
      setEnding(false);
    }
  };

  const forceUnlockCourt = async () => {
    if (!activeCourtStream) return;
    try {
      const res = await fetch(
        `${API_URL}/api/court-streams/${activeCourtStream.court_stream_id}/force-unlock`,
        { method: 'POST', headers: authHeaders }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchCourtStreams();
        onGameEnded?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to unlock court');
      }
    } catch {
      toast.error('Error unlocking court');
    }
  };

  const switchGame = async () => {
    if (!activeCourtStream) return;
    try {
      const res = await fetch(
        `${API_URL}/api/court-streams/${activeCourtStream.court_stream_id}/end-game`,
        { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ keep_live: true }) }
      );
      if (res.ok) {
        toast.success('Game detached — court ready for next game');
        await fetchCourtStreams();
        onGameEnded?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to switch');
      }
    } catch {
      toast.error('Error switching game');
    }
  };

  const moveGameHere = async () => {
    if (!activeCourtStream || !gameId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/court-streams/${activeCourtStream.court_stream_id}/move-game`,
        { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ game_id: gameId }) }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchCourtStreams();
        onGameEnded?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to move game');
      }
    } catch {
      toast.error('Error moving game');
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      console.warn('Clipboard API failed, using fallback');
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedField(field);
        toast.success('Copied!');
        setTimeout(() => setCopiedField(null), 2000);
      } catch (fallbackErr) {
        toast.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  // If not per_court mode or no event, don't render
  if (loading) {
    return (
      <div className="bg-zinc-900/80 border border-zinc-700 rounded-xl px-4 py-3 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-zinc-500 animate-spin" />
        <span className="text-sm text-zinc-500">Loading stream config...</span>
      </div>
    );
  }

  if (!streamConfig || streamConfig.stream_mode !== 'per_court') {
    return null; // Not in tournament mode, don't show this panel
  }

  if (!activeCourtStream) {
    return (
      <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl px-4 py-3" data-testid="court-stream-no-court">
        <div className="flex items-center gap-2 text-amber-400">
          <Tv className="w-4 h-4" />
          <span className="text-sm font-medium">Tournament Mode — No courts configured</span>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Go to the Admin Dashboard to add court streams for this event.
        </p>
      </div>
    );
  }

  const isThisGameActive = activeCourtStream.current_game_id === gameId;
  const anotherGameActive = activeCourtStream.current_game_id && !isThisGameActive;
  const isStreamLive = streamStatus?.is_live || false;

  const panel = (
    <div 
      className={`rounded-xl border transition-all ${
        isThisGameActive 
          ? 'bg-red-950/30 border-red-800/60' 
          : 'bg-zinc-900/80 border-zinc-700'
      }`}
      data-testid="court-stream-panel"
    >
      {/* Header - always visible */}
      <button 
        onClick={() => setExpanded(!expanded)} 
        className="w-full px-4 py-3 flex items-center justify-between"
        data-testid="court-stream-toggle"
      >
        <div className="flex items-center gap-3">
          <MonitorPlay className="w-5 h-5 text-cyan-400" />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">
                {activeCourtStream.court_name}
              </span>
              {isStreamLive ? (
                <Badge className="bg-emerald-600/80 text-white text-[10px] px-1.5 py-0">
                  <Wifi className="w-3 h-3 mr-0.5" /> CONNECTED
                </Badge>
              ) : (
                <Badge variant="outline" className="text-zinc-500 border-zinc-600 text-[10px] px-1.5 py-0">
                  <WifiOff className="w-3 h-3 mr-0.5" /> OFFLINE
                </Badge>
              )}
              {isThisGameActive && (
                <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0 animate-pulse">
                  <Radio className="w-3 h-3 mr-0.5" /> ON AIR
                </Badge>
              )}
            </div>
            <span className="text-xs text-zinc-500">Tournament Mode</span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        )}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-800/50">
          {/* Court Selector (if multiple courts) */}
          {courtStreams.length > 1 && (
            <div className="flex gap-2 pt-3 overflow-x-auto">
              {courtStreams.map(cs => (
                <button
                  key={cs.court_stream_id}
                  onClick={() => setActiveCourtStream(cs)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    cs.court_stream_id === activeCourtStream.court_stream_id
                      ? 'bg-cyan-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  {cs.court_name}
                  {cs.is_live && <Radio className="w-3 h-3 inline ml-1 text-red-400" />}
                </button>
              ))}
            </div>
          )}

          {/* Stream Credentials */}
          <div className="bg-zinc-800/60 rounded-lg p-3 space-y-2 mt-2">
            <div className="text-xs text-zinc-500 font-medium mb-1">STREAM CREDENTIALS</div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-zinc-500 shrink-0">RTMP:</span>
              <code className="text-[11px] text-emerald-400 font-mono truncate flex-1">
                {activeCourtStream.rtmps_url}
              </code>
              <button
                onClick={() => copyToClipboard('rtmp', activeCourtStream.rtmps_url)}
                className="p-1 hover:bg-zinc-700 rounded shrink-0"
                data-testid="copy-rtmp-url"
              >
                {copiedField === 'rtmp' ? (
                  <Check className="w-3 h-3 text-green-500" />
                ) : (
                  <Copy className="w-3 h-3 text-zinc-400" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-zinc-500 shrink-0">Key:</span>
              <code className="text-[11px] text-amber-400 font-mono truncate flex-1">
                ****{activeCourtStream.rtmps_stream_key?.slice(-8)}
              </code>
              <button
                onClick={() => copyToClipboard('key', activeCourtStream.rtmps_stream_key)}
                className="p-1 hover:bg-zinc-700 rounded shrink-0"
                data-testid="copy-stream-key"
              >
                {copiedField === 'key' ? (
                  <Check className="w-3 h-3 text-green-500" />
                ) : (
                  <Copy className="w-3 h-3 text-zinc-400" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-zinc-500 shrink-0">Full:</span>
              <code className="text-[11px] text-blue-400 font-mono truncate flex-1">
                {activeCourtStream.rtmps_url?.replace(/\/$/, '')}/{activeCourtStream.rtmps_stream_key}
              </code>
              <button
                onClick={() => copyToClipboard('full', `${activeCourtStream.rtmps_url?.replace(/\/$/, '')}/${activeCourtStream.rtmps_stream_key}`)}
                className="p-1 hover:bg-zinc-700 rounded shrink-0"
                data-testid="copy-full-url"
              >
                {copiedField === 'full' ? (
                  <Check className="w-3 h-3 text-green-500" />
                ) : (
                  <Copy className="w-3 h-3 text-zinc-400" />
                )}
              </button>
            </div>
          </div>

          {/* Share Stream Key Button */}
          <div className="flex gap-2">
            <Button
              onClick={() => setShowShareModal(true)}
              className="flex-1 h-10 bg-cyan-700 hover:bg-cyan-600 text-white font-semibold"
              data-testid="share-stream-key-btn"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Share Stream Key
            </Button>
            <Button
              onClick={() => setShowCameraPreview(!showCameraPreview)}
              variant={showCameraPreview ? "default" : "outline"}
              className={`h-10 px-3 ${
                showCameraPreview 
                  ? 'bg-emerald-700 hover:bg-emerald-600 text-white' 
                  : 'border-zinc-600 text-zinc-400 hover:text-white'
              }`}
              data-testid="toggle-camera-preview"
            >
              {showCameraPreview ? <CameraOff className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
            </Button>
          </div>

          {/* Camera Preview */}
          {showCameraPreview && activeCourtStream.playback_url && (
            <CameraPreview
              playbackUrl={activeCourtStream.playback_url}
              courtName={activeCourtStream.court_name}
              onClose={() => setShowCameraPreview(false)}
            />
          )}
          {showCameraPreview && !activeCourtStream.playback_url && (
            <div className="bg-zinc-800/60 rounded-lg p-4 text-center">
              <CameraOff className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
              <p className="text-xs text-zinc-500">No playback URL available. Start streaming to see the preview.</p>
            </div>
          )}

          {/* Game Status & Actions */}
          {isThisGameActive ? (
            /* This game is live on the court */
            <div className="space-y-2">
              <div className="bg-red-900/30 rounded-lg px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-red-500 animate-pulse" />
                  <span className="text-sm font-semibold text-red-300">This game is LIVE on {activeCourtStream.court_name}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={switchGame}
                  className="h-10 bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold"
                  data-testid="switch-game-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-1" /> Switch Game
                </Button>
                <Button
                  onClick={endGame}
                  disabled={ending}
                  className="h-10 bg-red-700 hover:bg-red-600 text-white text-sm font-bold"
                  data-testid="end-game-btn"
                >
                  {ending ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Square className="w-4 h-4 mr-1" />}
                  {ending ? 'Ending...' : 'End Game'}
                </Button>
              </div>
            </div>
          ) : anotherGameActive ? (
            /* Another game is active on this court */
            <div className="space-y-2">
              <div className="bg-amber-950/30 border border-amber-800/40 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-300">Another game is active on this court</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={moveGameHere}
                  className="h-10 bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-bold"
                  data-testid="move-game-here-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-1" /> Move Here
                </Button>
                <Button
                  onClick={forceUnlockCourt}
                  className="h-10 bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold"
                  data-testid="force-unlock-btn"
                >
                  <Unlock className="w-4 h-4 mr-1" /> Unlock
                </Button>
              </div>
            </div>
          ) : (
            /* No game active - show activate button */
            <Button
              onClick={activateGame}
              disabled={activating}
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-600 text-white text-base font-bold"
              data-testid="activate-game-btn"
            >
              {activating ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              {activating ? 'Activating...' : 'Activate Game on Stream'}
            </Button>
          )}

          {/* Game Queue */}
          {courtGames.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs text-zinc-500 font-medium">COURT SCHEDULE ({courtGames.length} games)</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {courtGames.map(g => {
                  const isCurrent = g.game_id === activeCourtStream.current_game_id;
                  const isThis = g.game_id === gameId;
                  return (
                    <div 
                      key={g.game_id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                        isCurrent 
                          ? 'bg-red-900/30 border border-red-800/50' 
                          : isThis 
                            ? 'bg-cyan-900/20 border border-cyan-800/40'
                            : 'bg-zinc-800/40'
                      }`}
                    >
                      {isCurrent && <Radio className="w-3 h-3 text-red-500 animate-pulse shrink-0" />}
                      {isThis && !isCurrent && <ArrowRight className="w-3 h-3 text-cyan-400 shrink-0" />}
                      <span className={`truncate ${isThis ? 'text-white font-semibold' : 'text-zinc-300'}`}>
                        {g.home_team_name} vs {g.away_team_name}
                      </span>
                      <Badge 
                        className={`ml-auto text-[10px] shrink-0 ${
                          g.status === 'live' ? 'bg-red-600' :
                          g.status === 'final' ? 'bg-zinc-600' :
                          'bg-zinc-700'
                        }`}
                      >
                        {g.status}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Refresh button */}
          <button
            onClick={async () => {
              await fetchCourtStreams();
              if (activeCourtStream?.court_stream_id) {
                await fetchCourtGames(activeCourtStream.court_stream_id);
              }
              if (activeCourtStream?.cloudflare_uid) {
                await checkStreamStatus(activeCourtStream.cloudflare_uid);
              }
              toast.success('Refreshed');
            }}
            className="text-xs text-zinc-600 hover:text-zinc-400 flex items-center gap-1 transition-colors"
            data-testid="refresh-court-stream"
          >
            <RefreshCw className={`w-3 h-3 ${checkingStatus ? 'animate-spin' : ''}`} />
            Refresh status
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {panel}
      {showShareModal && activeCourtStream && (
        <StreamShareModal
          rtmpUrl={activeCourtStream.rtmps_url}
          streamKey={activeCourtStream.rtmps_stream_key}
          srtUrl={activeCourtStream.srt_url}
          srtStreamId={activeCourtStream.srt_stream_id}
          courtName={activeCourtStream.court_name}
          isLive={isStreamLive}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
}
